import { Handle, NodeResizer, Position } from "reactflow"
import type { ReactNode } from "react"
import ReactMarkdown from "react-markdown"

interface ResizeParams {
  x: number
  y: number
  width: number
  height: number
}

interface NodeData {
  label: string
  description?: string
  tooltip?: string
  body: string
  color: string
  layerName: string
  type?: string
  icon?: string
  truncateDescriptions: boolean
  showFieldsOnNode: boolean
  fields?: Record<string, string>
  nodeWidth: number
  backgroundColor?: string
  searchQuery?: string
  isSelected?: boolean
  isEditing?: boolean
  onEditStart?: () => void
  onBodyCommit?: (body: string) => void
  onEditCancel?: () => void
  onResizeEnd?: (params: ResizeParams) => void
}

interface Props {
  data: NodeData
  selected: boolean
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

function highlightText(value: string, query?: string) {
  const trimmedQuery = query?.trim()
  if (!trimmedQuery) return value

  const pattern = new RegExp(`(${escapeRegExp(trimmedQuery)})`, "gi")
  return value.split(pattern).map((part, index) =>
    part.toLowerCase() === trimmedQuery.toLowerCase() ? (
      <mark key={`${part}-${index}`} className="rounded bg-yellow-200 px-0.5 text-slate-950">
        {part}
      </mark>
    ) : (
      part
    ),
  )
}

function highlightChildren(children: ReactNode, query?: string): ReactNode {
  if (typeof children === "string") return highlightText(children, query)
  if (typeof children === "number") return highlightText(String(children), query)
  if (Array.isArray(children)) return children.map((child) => highlightChildren(child, query))
  return children
}

export function CustomNode({ data, selected }: Props) {
  const isSelected = selected || data.isSelected
  const fields = data.fields ? Object.entries(data.fields) : []
  const hasTooltip = Boolean(data.tooltip)
  const startEditing = () => {
    data.onEditStart?.()
  }

  return (
    <div
      className={`group relative flex h-full flex-col rounded-lg px-4 py-3 cursor-pointer select-none transition-all border bg-card shadow-sm ${
        isSelected ? "shadow-lg" : "hover:shadow-md"
      }`}
      style={{
        width: "100%",
        height: "100%",
        backgroundColor: data.backgroundColor,
        borderTopColor: isSelected ? data.color : undefined,
        borderRightColor: isSelected ? data.color : undefined,
        borderBottomColor: isSelected ? data.color : undefined,
        borderLeftColor: data.color,
        borderLeftWidth: 4,
        boxShadow: isSelected ? `0 0 0 3px ${data.color}30` : undefined,
      }}
      onMouseDownCapture={(event) => {
        if (event.detail < 2) return
        event.stopPropagation()
        startEditing()
      }}
      onDoubleClick={(event) => {
        event.stopPropagation()
        startEditing()
      }}
    >
      {hasTooltip && !data.isEditing && (
        <div
          className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 hidden w-80 -translate-x-1/2 rounded-lg border border-border bg-popover px-3 py-2.5 text-left text-xs leading-relaxed text-popover-foreground shadow-xl group-hover:block"
          style={{ borderTopColor: data.color, borderTopWidth: 3 }}
        >
          <div className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.1em]" style={{ color: data.color }}>
            {data.type ?? data.layerName}
          </div>
          <div className="font-semibold text-foreground">{data.label}</div>
          <div className="mt-1.5 text-muted-foreground">{data.tooltip}</div>
        </div>
      )}
      <NodeResizer
        isVisible={isSelected}
        color={data.color}
        minWidth={140}
        minHeight={70}
        handleClassName="!h-2 !w-2"
        lineClassName="!border-border"
        onResizeEnd={(_, params) => data.onResizeEnd?.(params)}
      />
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: data.color, border: "2px solid white", width: 14, height: 14 }}
      />

      {data.type && (
        <span
          className="block text-[9px] font-bold tracking-[0.08em] uppercase mb-1"
          style={{ color: data.color, opacity: 0.85 }}
        >
          {data.type}
        </span>
      )}

      {data.isEditing ? (
        <textarea
          autoFocus
          defaultValue={data.body}
          className="nodrag nowheel min-h-0 flex-1 w-full resize-none rounded border border-border bg-background px-2 py-1.5 text-sm leading-relaxed text-foreground outline-hidden focus:border-primary"
          onBlur={(event) => data.onBodyCommit?.(event.target.value)}
          onClick={(event) => event.stopPropagation()}
          onDoubleClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
              event.preventDefault()
              data.onBodyCommit?.(event.currentTarget.value)
            }
            if (event.key === "Escape") {
              event.preventDefault()
              data.onEditCancel?.()
            }
          }}
        />
      ) : (
        <div className="text-sm leading-relaxed">
          {data.icon && <span className="mr-1.5 text-base">{data.icon}</span>}
          <ReactMarkdown
            components={{
              h1: ({ children }) => (
                <h1 className="mb-2 text-lg font-bold leading-tight text-foreground">
                  {highlightChildren(children, data.searchQuery)}
                </h1>
              ),
              h2: ({ children }) => (
                <h2 className="mb-1.5 text-base font-bold leading-tight text-foreground">
                  {highlightChildren(children, data.searchQuery)}
                </h2>
              ),
              h3: ({ children }) => (
                <h3 className="mb-1 text-sm font-semibold leading-tight text-foreground">
                  {highlightChildren(children, data.searchQuery)}
                </h3>
              ),
              p: ({ children }) => (
                <p className="mb-1 last:mb-0 text-foreground">{highlightChildren(children, data.searchQuery)}</p>
              ),
              strong: ({ children }) => (
                <strong className="font-semibold text-foreground">
                  {highlightChildren(children, data.searchQuery)}
                </strong>
              ),
              em: ({ children }) => (
                <em className="text-foreground">{highlightChildren(children, data.searchQuery)}</em>
              ),
              ul: ({ children }) => (
                <ul className="ml-4 list-disc text-muted-foreground">
                  {highlightChildren(children, data.searchQuery)}
                </ul>
              ),
              ol: ({ children }) => (
                <ol className="ml-4 list-decimal text-muted-foreground">
                  {highlightChildren(children, data.searchQuery)}
                </ol>
              ),
              li: ({ children }) => <li className="my-0.5">{highlightChildren(children, data.searchQuery)}</li>,
              code: ({ children }) => (
                <code className="rounded bg-secondary px-1 py-0.5 text-[11px] text-foreground">
                  {highlightChildren(children, data.searchQuery)}
                </code>
              ),
            }}
          >
            {data.body}
          </ReactMarkdown>
        </div>
      )}

      {data.showFieldsOnNode && fields.length > 0 && (
        <div className="mt-2 border-t border-border pt-1.5">
          {fields.map(([k, v]) => (
            <div key={k} className="flex justify-between text-[10px] leading-[1.8]">
              <span className="text-muted-foreground">{k}</span>
              <span className="text-foreground font-medium">{v}</span>
            </div>
          ))}
        </div>
      )}

      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: data.color, border: "2px solid white", width: 14, height: 14 }}
      />
    </div>
  )
}
